import { computed, ref, type ComputedRef } from 'vue'
import { initApiService } from '@/services/init'
import { config } from '@/config'
import { useUserProfileStore } from '@/stores/userProfile'
import { logUnexpectedFailure } from '@/utils/errorHandling'

interface CachedAvailability {
  organizationId: string
  inboxAvailable: boolean
}

// Shared by the side menu and the route guard, so the answer is fetched once per organisation
// rather than once per caller. An organisation switch reloads the page; the key covers a sign-in
// as someone else in the same tab.
const cached = ref<CachedAvailability | null>(null)
let inFlight: { organizationId: string; promise: Promise<void> } | null = null

function currentOrganizationId(): string | null {
  return useUserProfileStore().profile?.organizationId ?? null
}

async function fetchFor(organizationId: string): Promise<void> {
  try {
    const response = await initApiService().userIntegrations.getMyZorgMailAvailability()
    cached.value = { organizationId, inboxAvailable: response.data.inboxAvailable }
  } catch (err) {
    // No answer means no inbox; the next caller asks again.
    logUnexpectedFailure('Failed to read ZorgMail inbox availability:', err)
  }
}

export interface UseZorgMailInboxAvailabilityReturn {
  /**
   * Whether the practice's ZorgMail inbox is offered: the runtime flag, and a mailbox login that
   * has synced at least once. False until loaded. Who may *read* it is a permission, asked apart.
   */
  inboxAvailable: ComputedRef<boolean>
  /** Loads the answer for the current organisation unless it is already known. */
  ensureLoaded: () => Promise<void>
  /** Asks again — after the mailbox login was saved or removed. */
  refresh: () => Promise<void>
}

export function useZorgMailInboxAvailability(): UseZorgMailInboxAvailabilityReturn {
  const inboxAvailable = computed(() => {
    const organizationId = currentOrganizationId()
    return (
      config.zorgmailInboxEnabled &&
      organizationId !== null &&
      cached.value?.organizationId === organizationId &&
      cached.value.inboxAvailable
    )
  })

  function load(force: boolean): Promise<void> {
    const organizationId = currentOrganizationId()
    if (!config.zorgmailInboxEnabled || organizationId === null) return Promise.resolve()
    if (!force && cached.value?.organizationId === organizationId) return Promise.resolve()
    if (!force && inFlight?.organizationId === organizationId) return inFlight.promise

    const promise = fetchFor(organizationId).finally(() => {
      if (inFlight?.promise === promise) inFlight = null
    })
    inFlight = { organizationId, promise }
    return promise
  }

  return {
    inboxAvailable,
    ensureLoaded: () => load(false),
    refresh: () => load(true),
  }
}

/** Test seam: forgets the cached answer. */
export function resetZorgMailInboxAvailability(): void {
  cached.value = null
  inFlight = null
}
